import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router";
import { Box, Sheet, Typography, FormControl, FormLabel, Input, Button, Alert } from "@mui/joy";
import { motion } from "framer-motion";
import { useAdminAuth } from "../AdminAuthContext";
import { LOGO_URL } from "../AdminApp";

const hideOnError = (e) => { e.currentTarget.style.display = "none"; };

// Плавающие цветные «пятна» на фоне (совпадают с палитрой шкалы медиа).
const BLOBS = [
  { c: "#4C6FFF", size: 460, left: "-8%", top: "-12%", dur: 20 },
  { c: "#00B8A9", size: 380, left: "68%", top: "4%", dur: 24 },
  { c: "#9B5DE5", size: 420, left: "12%", top: "62%", dur: 28 },
  { c: "#F5A623", size: 320, left: "74%", top: "70%", dur: 22 },
];

export default function LoginPage() {
  const { staff, login, loading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Уже авторизован → на дашборд
  if (!loading && staff) {
    const to = location.state?.from?.pathname || "/admin";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const to = location.state?.from?.pathname || "/admin";
      navigate(to, { replace: true });
    } catch (err) {
      setError(err.message || "Не удалось войти");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, overflow: "hidden", bgcolor: "#eef1f8" }}>
      {/* Анимированный фон */}
      {BLOBS.map((b, i) => (
        <Box
          key={i}
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 0.45,
            x: [0, 30, -25, 0],
            y: [0, -28, 22, 0],
            scale: [1, 1.12, 0.94, 1],
          }}
          transition={{ duration: b.dur, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute", left: b.left, top: b.top,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.c, filter: "blur(90px)", pointerEvents: "none",
          }}
        />
      ))}

      {/* Карточка входа */}
      <Box sx={{
        position: "relative", zIndex: 1, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center", p: 2,
      }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 22, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          sx={{ width: 400, maxWidth: "100%" }}
        >
          <Sheet
            variant="plain"
            sx={{
              p: { xs: 3, sm: 4 }, borderRadius: "20px", border: "none",
              bgcolor: "rgba(255,255,255,0.82)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 24px 70px rgba(31,42,74,0.18)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", mb: 2.5 }}>
              <Box component="img" src={LOGO_URL} alt="MagicBoard" onError={hideOnError}
                   sx={{ height: 46, width: "auto", objectFit: "contain", mb: 1.25 }} />
              <Typography level="h4" sx={{ mb: 0.25 }}>Вход в админку</Typography>
              <Typography level="body-sm" color="neutral">Введите email и пароль сотрудника</Typography>
            </Box>

            {error && <Alert color="danger" variant="soft" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={onSubmit}>
              <FormControl sx={{ mb: 1.5 }}>
                <FormLabel>Email</FormLabel>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       autoComplete="username" required size="lg" />
              </FormControl>
              <FormControl sx={{ mb: 2.5 }}>
                <FormLabel>Пароль</FormLabel>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       autoComplete="current-password" required size="lg" />
              </FormControl>
              <Button type="submit" fullWidth size="lg" loading={submitting} disabled={!email || !password}>
                Войти
              </Button>
            </form>
          </Sheet>
        </Box>
      </Box>
    </Box>
  );
}